import { Controller } from "@hotwired/stimulus"

// Provides a lightweight async autocomplete for large datasets (e.g. athletes per event).
export default class extends Controller {
  static targets = ["input", "hidden", "results", "clear"]
  static values = {
    url: String,
    param: { type: String, default: "q" },
    minLength: { type: Number, default: 2 },
    initialLabel: String
  }

  connect() {
    this.results = []
    this.timeout = null
    if (this.hasInitialLabelValue && this.hasInputTarget) {
      this.inputTarget.value = this.initialLabelValue
    }
    this.setExpanded(false)
    this.boundClose = this.close.bind(this)
    document.addEventListener("click", this.boundClose)
  }

  disconnect() {
    clearTimeout(this.timeout)
    document.removeEventListener("click", this.boundClose)
  }

  open() {
    if (!this.hasResultsTarget) return
    if (this.resultsTarget.childElementCount > 0) {
      this.resultsTarget.classList.remove("hidden")
      this.setExpanded(true)
    }
  }

  close(event) {
    if (!this.hasResultsTarget) return
    if (event && this.element.contains(event.target)) return
    this.resultsTarget.classList.add("hidden")
    this.setExpanded(false)
  }

  handleKeydown(event) {
    if (event.key === "Escape") {
      this.close()
    }
  }

  search() {
    if (!this.hasInputTarget || !this.hasResultsTarget) return
    const term = this.inputTarget.value.trim()

    if (term.length < this.minLengthValue) {
      this.clearResults()
      return
    }

    clearTimeout(this.timeout)
    this.timeout = setTimeout(() => this.fetchResults(term), 200)
  }

  async fetchResults(term) {
    if (!this.hasUrlValue) return

    this.currentTerm = term
    this.showStatus("Searching…")

    try {
      const url = new URL(this.urlValue, window.location.origin)
      url.searchParams.set(this.paramValue, term)
      const response = await fetch(url.toString(), {
        headers: { Accept: "application/json" }
      })

      if (!response.ok) throw new Error(`${response.status}: ${response.statusText}`)

      const data = await response.json()
      if (this.currentTerm !== term) return

      this.results = Array.isArray(data) ? data : []
      this.renderResults()
    } catch (error) {
      console.error("Async select fetch failed", error)
      this.showStatus("Unable to load results")
    }
  }

  renderResults() {
    if (!this.hasResultsTarget) return

    this.resultsTarget.innerHTML = ""

    if (this.results.length === 0) {
      this.showStatus("No matches found")
      return
    }

    this.results.forEach((result) => {
      const button = document.createElement("button")
      button.type = "button"
      button.className = "block w-full px-4 py-3 text-left text-sm text-slate-700 hover:bg-slate-50 focus:bg-slate-100 focus:outline-none"
      button.dataset.id = result.id
      const baseLabel = result.label || "Unnamed athlete"
      const subtitle = result.subtitle || ""
      const combined = subtitle ? `${baseLabel} · ${subtitle}` : baseLabel
      button.dataset.label = combined
      button.dataset.subtitle = subtitle

      button.innerHTML = `
        <div class="flex flex-col">
          <span class="font-medium text-slate-900">${this.escape(result.label || "Unnamed athlete")}</span>
          ${result.subtitle ? `<span class="text-xs text-slate-500">${this.escape(result.subtitle)}</span>` : ""}
          ${result.active === false ? '<span class="mt-1 inline-flex w-fit items-center gap-1 rounded-full bg-amber-100 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-amber-700">Hidden</span>' : ""}
        </div>
      `

      this.resultsTarget.appendChild(button)
    })

    this.resultsTarget.classList.remove("hidden")
    this.setExpanded(true)
  }

  select(event) {
    const button = event.target.closest("button[data-id]")
    if (!button) return

    const id = button.dataset.id
    const label = button.dataset.label || ""

    if (this.hasHiddenTarget) {
      this.hiddenTarget.value = id
      this.hiddenTarget.dispatchEvent(new Event("change", { bubbles: true }))
    }

    if (this.hasInputTarget) {
      this.inputTarget.value = label
    }

    this.resultsTarget.classList.add("hidden")
    this.setExpanded(false)
  }

  clear(event) {
    event.preventDefault()
    if (this.hasHiddenTarget) {
      this.hiddenTarget.value = ""
      this.hiddenTarget.dispatchEvent(new Event("change", { bubbles: true }))
    }
    if (this.hasInputTarget) {
      this.inputTarget.value = ""
      this.inputTarget.focus()
    }
    this.clearResults()
  }

  clearResults() {
    if (!this.hasResultsTarget) return
    this.results = []
    this.resultsTarget.innerHTML = ""
    this.resultsTarget.classList.add("hidden")
    this.setExpanded(false)
  }

  showStatus(message) {
    if (!this.hasResultsTarget) return
    this.resultsTarget.innerHTML = `<p class="px-4 py-3 text-sm text-slate-500">${this.escape(message)}</p>`
    this.resultsTarget.classList.remove("hidden")
    this.setExpanded(true)
  }

  escape(text) {
    return String(text)
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;")
      .replace(/"/g, "&quot;")
      .replace(/'/g, "&#39;")
  }

  setExpanded(value) {
    if (this.hasInputTarget) {
      this.inputTarget.setAttribute("aria-expanded", value ? "true" : "false")
    }
  }
}
